import { Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Lock, Download, ExternalLink } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/seo/SEO";
import { useMember } from "@/context/MemberAuthContext";
import { products } from "@/data/products";
import { TIERS } from "@/data/tiers";

const Library = () => {
  const { member } = useMember();

  if (member === undefined) {
    return (
      <main className="min-h-screen bg-[#F7F7F4]">
        <Header />
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#111111]/40">
            Loading…
          </div>
        </section>
      </main>
    );
  }

  if (member === null) {
    return <Navigate to="/login" replace />;
  }

  const hasAccess = !!member.library_access;
  const studio = TIERS.find((t) => t.id === "studio");

  return (
    <main data-testid="library-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO
        title="Library"
        description="Members-only library of every Vaymilo digital product."
        path="/library"
        noindex
      />
      <Header />

      {/* Hero */}
      <section className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="overline">— Library</div>
          <h1
            data-testid="library-headline"
            className="mt-5 font-sora text-[44px] sm:text-[56px] lg:text-[72px] font-extrabold tracking-tight leading-[1.02] text-[#111111]"
          >
            {hasAccess ? "Everything we ship." : "What you'd unlock."}
          </h1>
          <p className="mt-6 max-w-2xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70">
            {hasAccess
              ? "Your Studio membership includes every Vaymilo product — past and future. Download anything below, any time."
              : "Studio members get the entire library — every kit, every monthly drop. Here's a peek at what's inside."}
          </p>
        </div>
      </section>

      {/* Gating banner (only for free/insider) */}
      {!hasAccess && (
        <section data-testid="library-gate" className="bg-white border-b border-[#E7E7E2]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12">
            <div className="bg-[#111111] text-white rounded-2xl p-7 md:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10">
                  <Lock size={18} strokeWidth={2.25} />
                </div>
                <div>
                  <div className="font-sora text-[20px] md:text-[22px] font-bold tracking-tight">
                    Library access is locked.
                  </div>
                  <p className="mt-2 font-manrope text-[14.5px] text-white/70 leading-relaxed max-w-xl">
                    Subscribe to Studio (${studio?.priceMonthly}/mo) to unlock
                    every kit below — plus every future drop, automatically.
                  </p>
                </div>
              </div>
              <Link
                to="/pricing"
                data-testid="library-unlock-cta"
                className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[14.5px] px-5 py-3 rounded-lg transition-colors whitespace-nowrap"
              >
                Unlock library
                <ArrowUpRight size={14} strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Product grid */}
      <section className="bg-[#F7F7F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <article
                key={p.slug}
                data-testid={`library-product-${p.slug}`}
                className="bg-white border border-[#E7E7E2] rounded-2xl p-6 md:p-7 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/45">
                    Kit · {p.no}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#2563EB]">
                    {p.category}
                  </span>
                </div>
                <h3 className="mt-4 font-sora text-[22px] font-bold text-[#111111] tracking-tight leading-tight">
                  {p.name}
                </h3>
                <p className="mt-3 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
                  {p.tagline}
                </p>

                <div className="mt-6 pt-5 border-t border-[#E7E7E2] flex items-center justify-between gap-3">
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`library-detail-${p.slug}`}
                    className="font-manrope text-[13.5px] text-[#111111] hover:text-[#2563EB] border-b border-[#E7E7E2] hover:border-[#2563EB] pb-0.5"
                  >
                    Details →
                  </Link>
                  {hasAccess ? (
                    <button
                      type="button"
                      disabled
                      data-testid={`library-download-${p.slug}`}
                      title="Download will be wired to the next drop"
                      className="inline-flex items-center gap-1.5 bg-[#2563EB]/10 text-[#2563EB] font-manrope font-medium text-[13px] px-3.5 py-2 rounded-lg cursor-not-allowed"
                    >
                      <Download size={12} strokeWidth={2.25} />
                      Download
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 font-manrope text-[12.5px] text-[#111111]/45">
                      <Lock size={12} strokeWidth={2.25} />
                      Studio
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>

          {/* Manage subscription footer link */}
          {hasAccess && (
            <div className="mt-12 flex items-center justify-center gap-3 font-manrope text-[14px] text-[#111111]/55">
              <span>Need to update your billing?</span>
              <a
                href="https://shop.vaymilo.com/account"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="library-manage-billing"
                className="inline-flex items-center gap-1.5 text-[#111111] hover:text-[#2563EB] border-b border-[#E7E7E2] hover:border-[#2563EB] pb-0.5"
              >
                Manage on Fourthwall
                <ExternalLink size={11} strokeWidth={2.25} />
              </a>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Library;
